import type {
  CostPerUnitVM,
  GetOfferDuration,
  GetOffersNormalizedResponse,
  IncentiveType,
  Offer,
} from '@innogy/become-a-customer/shared/interfaces';
import {
  type EnergyType,
  type OmtOfferSet,
  ConsumptionRestriction,
  CustomerType,
  type OmtOffer,
} from '@innogy/server/get-offer/interfaces';
import type { ContractType, ProductDuration } from '@essent/common';

const OFFERID_ONE_YEAR = '102402-60201-1';
const OFFERID_THREE_YEARS = '102402-60201-3';

/**
  [ImportEnumAsTypeExplanation]:
  During Angular upgrade we found out that import angular code in NestJS libraries were not
  working anymore due to packaging. Therefor we had to refactor all interfaces/enum imports to
  import type. However, for enums this will not work and therefor we had to introduce
  this dirty workaround. I'm sooo soryy.
*/
const productDurationOne = '1' as ProductDuration;
const productDurationThree = '3' as ProductDuration;
const productDurationFive = '5' as ProductDuration;
const productDurationFlex = 'flex' as ProductDuration;

const energyTypeE = 'electricity' as EnergyType;
const energyTypeG = 'gas' as EnergyType;

const incentiveCashback = 'Cashback' as IncentiveType;

export const mockOfferId = OFFERID_ONE_YEAR;
export const mockPartnerId = '10928321';
export const mockMemberGetMemberSaleId = '639717f45849b8f32d0009cc';
export const mockPromoCodeId = 'extrakorting';

export const mockCampaignUsp =
  'Interessant als je zelf een cadeau wil uitzoeken, kies uit veel merken';
export const mockCustomUsp =
  'Bij deelname aan ons klantenprogramma Thuisvoordeel direct korting op stroom';
export const mockIncentiveUsp = 'Kies zelf je caudeaukaart t.w.v. €250';
export const mockFallbackUsp = 'Dit product heeft geen incentive met een usp';
export const mockUsps = [mockCampaignUsp, mockIncentiveUsp, mockCustomUsp];

export const mockOfferSet: OmtOfferSet = {
  offers: [
    // Should only be returned when there is a return supply provided
    {
      offerId: '102402-60201-1',
      campaignId: '140474',
      incentiveId: '60201',
      partnerId: '1',
      duration: productDurationOne,
      consumptionRestriction: ConsumptionRestriction.HasReturnSupply,
    },
    // Should only be returned for existing customers
    {
      offerId: '102402-60201-3',
      campaignId: '140474',
      incentiveId: '60201',
      partnerId: '1',
      duration: productDurationThree,
      customer: CustomerType.ExistingCustomer,
    },
    // Should only be returned for new customers
    {
      offerId: '102402-60201-5',
      campaignId: '140474',
      incentiveId: '60201',
      partnerId: '1',
      duration: productDurationFive,
      customer: CustomerType.NewCustomer,
    },
    // Should only be returned for new customer that have no return supply
    {
      offerId: '102402-60201-f',
      campaignId: '140474',
      incentiveId: '60201',
      partnerId: '1',
      duration: productDurationFlex,
      consumptionRestriction: ConsumptionRestriction.NoReturnSupply,
      customer: CustomerType.ExistingCustomer,
    },
  ],
};

export const mockNonCustomerOfferset: OmtOfferSet = {
  offers: [mockOfferSet.offers[0], mockOfferSet.offers[2]],
};

export const mockCustomerOfferset: OmtOfferSet = {
  offers: [
    mockOfferSet.offers[0],
    mockOfferSet.offers[1],
    mockOfferSet.offers[3],
  ],
};

export const mockOfferWithoutIncentive: OmtOffer = {
  offerId: '102402-60201-1',
  campaignId: '140474',
  partnerId: '1',
  duration: productDurationOne,
  customer: CustomerType.NewCustomer,
};

export const mockOffer: Offer = {
  offerId: OFFERID_ONE_YEAR,
  incentiveId: '60201',
  campaignId: '102402',
  partnerId: '1',
  isCustomer: false,
  offerOverviews: [
    {
      energyType: energyTypeE,
      consumptionPrices: [
        {
          amount: 0.21538,
          description: 'Totaal elektriciteit Normaaltarief (1 t/m 2.900 kWh):',
          fromDate: '01-10',
          toDate: '30-09',
        },
        {
          amount: 0.19538,
          description: 'Totaal elektriciteit Daltarief (1 t/m 2.900 kWh):',
          fromDate: '01-10',
          toDate: '30-09',
        },
      ],
      priceGroups: [
        {
          id: '010',
          description: 'Leveringskosten elektriciteit',
          prices: [
            {
              id: '1',
              unit: 'kWh',
              unitPrice: 0.06413,
              description: 'Variabele leveringskosten Normaaltarief',
              expectedPeriodAmount: 0,
              fromDate: '01-10',
              toDate: '30-09',
            },
            {
              id: '2',
              unit: 'kWh',
              unitPrice: 0.06413,
              description: 'Variabele leveringskosten Daltarief',
              expectedPeriodAmount: 0,
              fromDate: '01-10',
              toDate: '30-09',
            },
            {
              id: '5',
              unit: 'dag',
              unitPrice: 0.19693,
              description: 'Vaste leveringskosten (€ 5.99 /maand)',
              expectedPeriodAmount: 71.88,
              fromDate: '01-10',
              toDate: '30-09',
            },
            {
              id: '12',
              unit: 'dag',
              unitPrice: 0.06557,
              description:
                'Terugleverkosten 1.001 t/m 1.250 kWh (€ 2.00 /maand)',
              expectedPeriodAmount: 24.0,
              fromDate: '01-10',
              toDate: '30-09',
            },
          ],
          totalPrice: {
            expectedMonthlyAmount: -3.39,
            expectedYearlyAmount: -40.62,
          },
        },
        {
          id: '020',
          description: 'Netbeheerkosten',
          prices: [
            {
              id: '1',
              unit: 'dag',
              unitPrice: 0.63477,
              description:
                'voor Enexis B.V. : Capaciteitstarief t/m 3 x 25A of t/m 1 x 80A (€ 232.33 /jaar)',
              expectedPeriodAmount: 232.33,
              fromDate: '01-10',
              toDate: '30-09',
            },
          ],
          totalPrice: {
            expectedMonthlyAmount: 19.36,
            expectedYearlyAmount: 232.33,
          },
        },
        {
          id: '030',
          description: 'Belasting en toeslagen',
          prices: [
            {
              id: '1',
              unit: 'kWh',
              unitPrice: 0.11822,
              description: 'Energiebelasting zone 1 (1 t/m 2.900 kWh)',
              expectedPeriodAmount: 0,
              fromDate: '01-10',
              toDate: '30-09',
            },
            {
              id: '4',
              unit: 'kWh',
              unitPrice: 0.03303,
              description: 'Energiebelasting zone 2 (2.901 t/m 10.000 kWh)',
              expectedPeriodAmount: 0,
              fromDate: '01-10',
              toDate: '30-09',
            },
            {
              id: '7',
              unit: 'dag',
              unitPrice: -1.44036,
              description: 'Vermindering energiebelasting (€ -527.17 / jaar)',
              expectedPeriodAmount: -527.17,
              fromDate: '01-10',
              toDate: '30-09',
            },
          ],
          totalPrice: {
            expectedMonthlyAmount: -43.93,
            expectedYearlyAmount: -527.17,
          },
        },
      ],
      totalPrice: {
        expectedMonthlyAmount: -27.96,
        expectedYearlyAmount: -335.46,
      },
      financialIncentives: [
        {
          id: '57601',
          description:
            'Jaarlijks oplopende korting tot max 112.50 euro per product (37.50-37.50-37.50)',
        },
      ],
    },
    {
      energyType: energyTypeG,
      consumptionPrices: [
        {
          amount: 0.76142,
          description: 'Totaal tarief gas (1 t/m 1.000 m3):',
          fromDate: '01-10',
          toDate: '30-09',
        },
      ],
      priceGroups: [
        {
          id: '010',
          description: 'Leveringskosten gas',
          prices: [
            {
              id: '1',
              unit: 'm3',
              unitPrice: 0.26463,
              description: 'Variabele leveringskosten (G1/gasregio 04)',
              expectedPeriodAmount: 0,
              fromDate: '01-10',
              toDate: '30-09',
            },
            {
              id: '5',
              unit: 'dag',
              unitPrice: 0.2298,
              description: 'Vaste leveringskosten (€ 6.99 /maand)',
              expectedPeriodAmount: 83.88,
              fromDate: '01-10',
              toDate: '30-09',
            },
          ],
          totalPrice: {
            expectedMonthlyAmount: -3.39,
            expectedYearlyAmount: -40.62,
          },
        },
        {
          id: '020',
          description: 'Netbeheerkosten',
          prices: [
            {
              id: '1',
              unit: 'dag',
              unitPrice: 0.47964,
              description:
                'voor Enexis B.V. : Capaciteitstarief t/m 10 m3/uur, stand.jaarv. 500-4.000 m3 (€ 175.55 /jaar)',
              expectedPeriodAmount: 175.55,
              fromDate: '01-10',
              toDate: '30-09',
            },
          ],
          totalPrice: {
            expectedMonthlyAmount: 14.63,
            expectedYearlyAmount: 175.55,
          },
        },
        {
          id: '030',
          description: 'Belasting en toeslagen',
          prices: [
            {
              id: '1',
              unit: 'm3',
              unitPrice: 0.40301,
              description: 'Energiebelasting zone 1 (0 t/m 1.000 m3)',
              expectedPeriodAmount: 0,
              fromDate: '01-10',
              toDate: '30-09',
            },
            {
              id: '4',
              unit: 'm3',
              unitPrice: 0.09378,
              description: 'Energiebelasting zone 2 (1.001 t/m 170.000 m3)',
              expectedPeriodAmount: 0,
              fromDate: '01-10',
              toDate: '30-09',
            },
          ],
          totalPrice: { expectedMonthlyAmount: 0, expectedYearlyAmount: 0 },
        },
      ],
      totalPrice: {
        expectedMonthlyAmount: 11.24,
        expectedYearlyAmount: 134.93,
      },
      financialIncentives: [
        {
          id: '57601',
          description:
            'Jaarlijks oplopende korting tot max 112.50 euro per product (37.50-37.50-37.50)',
        },
      ],
    },
  ],
  expectedYearlyAmount: 261.84,
  expectedMonthlyAmount: 21.82,
  beforeDiscountExpectedYearlyAmount: 381.84,
  beforeDiscountExpectedMonthlyAmount: 31.82,
  budgetBillAmount: 22,
  discountPrice: 120,
  duration: productDurationOne,
  durationTitle: '1 jaar',
  commodities: [energyTypeE, energyTypeG],
  generalTermsAndConditionsUrl: 'example.com/general_terms.pdf',
  qualityCriteriaUrl: 'example.com/quality_criteria.pdf',
  productConditionsUrl: 'example.com/product_conditions.pdf',
  actionConditionsUrl: 'example.com/action_conditions.pdf',
  productTitle: 'Groene Stroom',
  productSubtitle: '',
  moreInfoUrl: '/beta/e2e/offer-more-info-minimal-page',
  moreInfoTitle: 'Product meer info titel',
  moreInfoCtaButtonText: 'Ik kies deze aanbieding',
  incentiveTitle: '+ 120 euro cashback',
  incentiveValue: 120,
  incentiveImage: '/-/media/energiedirect/cashback-075-groen.ashx',
  incentiveType: incentiveCashback,
  incentiveInfoUrl: 'https://www.essent.nl/content/particulier/index.html',
  incentiveSummary: '',
  incentiveFullDescription: '',
  vatIncluded: true,
  isHighlighted: true,
  isHighlightedLabel: 'Leukste offer',
  campaignUsp: mockCampaignUsp,
  customUsp: mockCustomUsp,
  incentiveUsp: mockIncentiveUsp,
  noIncentiveFallbackUsp: mockFallbackUsp,
  noIncentiveFallbackInfoUrl:
    'https://www.essent.nl/content/particulier/fallback.html',
  usps: mockUsps,
  disclaimerContent: 'disclaimer content mock value',
  usageType: '2',
  isDynamicProduct: false,
  contractType: 'fixed' as ContractType,
  dynamicPricingTitle: 'Dynamische energie',
};

export const mockOffers: Offer[] = [
  mockOffer,
  {
    ...mockOffer,
    offerId: OFFERID_THREE_YEARS,
    isCustomer: true,
    duration: productDurationThree,
    durationTitle: '3 jaar',
    productTitle: 'Groene Stroom',
  },
  {
    ...mockOffer,
    offerId: '102402-60201-5',
    duration: productDurationFive,
    durationTitle: '5 jaar',
    productTitle: 'Groene Stroom',
  },
  {
    ...mockOffer,
    offerId: '102402-60201-f',
    isCustomer: true,
    duration: productDurationFlex,
    durationTitle: 'Maandelijks opzegbaar',
    productTitle: 'Groene Stroom',
  },
  {
    ...mockOffer,
    usageType: '6',
  },
  {
    ...mockOffer,
    offerId: '102402-60201-5',
    duration: productDurationFive,
    durationTitle: '5 jaar',
    productTitle: 'Groene Stroom',
    usageType: '6',
  },
  {
    ...mockOffer,
    usageType: '5',
  },
  {
    ...mockOffer,
    offerId: '102402-60201-5',
    duration: productDurationFive,
    durationTitle: '5 jaar',
    productTitle: 'Groene Stroom',
    usageType: '5',
  },
];

export const mockOffersNonCustomer: Offer[] = [mockOffers[0], mockOffers[2]];
export const mockOffersNonCustomerUltra: Offer[] = [
  mockOffers[4],
  mockOffers[5],
];
export const mockOffersNonCustomerHigh: Offer[] = [
  mockOffers[6],
  mockOffers[7],
];

export const mockOffersCustomer: Offer[] = [
  mockOffers[0],
  mockOffers[1],
  mockOffers[3],
];

export const offerPayload: GetOffersNormalizedResponse = {
  offerIds: [OFFERID_ONE_YEAR],
  offers: {
    [OFFERID_ONE_YEAR]: mockOffers[0],
  },
  durations: {
    filter: 'default_duration',
    present: [
      { duration: productDurationOne, durationTitle: '1 jaar' },
      { duration: productDurationThree, durationTitle: '3 jaar' },
    ],
    retrieved: [productDurationOne],
  },
  offerset: 'mock-offerset',
  isCustomer: false,
};

export const offerPayloadSingleOffer: GetOffersNormalizedResponse = {
  offerIds: [OFFERID_ONE_YEAR],
  offers: {
    [OFFERID_ONE_YEAR]: mockOffers[0],
  },
  durations: {
    filter: 'default_duration',
    present: [{ duration: productDurationOne, durationTitle: '1 jaar' }],
    retrieved: [productDurationOne],
  },
  offerset: 'mock-offerset',
  isCustomer: false,
};

export const alternativeOfferPayload = {
  offerIds: ['204201-10206-3', '474041-10206-1'],
  customer: false,
  offers: {
    '474041-10206-1': mockOffers[0],
    '204201-10206-3': mockOffers[1],
  },
} as unknown as GetOffersNormalizedResponse;

export const costPerUnitVM: CostPerUnitVM = {
  electricity: {
    fixedDeliveryPeriodAmount: 71.88,
    fixedDeliveryDailyCosts: 0.19693,
    fixedRedeliveryPeriodAmount: 24,
    fixedRedeliveryDailyCosts: 0.06557,
    fixedRedeliveryCostsScale: '1.001 t/m 1.250 kWh',
    tariffPeriods: [
      {
        description: '<strong>normaal</strong><br />1 t/m 2.900 kWh',
        summerPrice: 0.21538,
        winterPrice: undefined,
      },
      {
        description: '<strong>dal</strong><br />1 t/m 2.900 kWh',
        summerPrice: 0.19538,
        winterPrice: undefined,
      },
    ],
  },
  gas: {
    fixedDeliveryPeriodAmount: 83.88,
    fixedDeliveryDailyCosts: 0.2298,
    tariffPeriods: [
      {
        description: '1 t/m 1.000 m3',
        summerPrice: 0.76142,
        winterPrice: undefined,
      },
    ],
  },
};

export const mockMonoElectricityOffer: Offer = {
  ...mockOffer,
  offerOverviews: [mockOffer.offerOverviews[0]],
  commodities: [energyTypeE],
};

export const mockMonoGasOffer: Offer = {
  ...mockOffer,
  offerOverviews: [mockOffer.offerOverviews[1]],
  commodities: [energyTypeG],
};

export const monoOfferPayload = {
  offerIds: [OFFERID_ONE_YEAR],
  customer: false,
  offers: {
    [OFFERID_ONE_YEAR]: mockMonoElectricityOffer,
  },
} as unknown as GetOffersNormalizedResponse;

export const mockAppendOffersSingleOffer = {
  customer: false,
  offers: [mockOffer],
  offerset: 'mock-offerset',
  durations: {
    present: [
      {
        duration: productDurationOne,
        durationTitle: '1 jaar',
      },
    ],
    retrieved: [productDurationOne],
  },
};

export const mockAppendTwoOffers = {
  customer: false,
  offers: [mockOffers[0], mockOffers[1]],
  offerset: 'mock-offerset',
  durations: {
    present: [
      {
        duration: productDurationOne,
        durationTitle: '1 jaar',
      },
      {
        duration: productDurationThree,
        durationTitle: '1 jaar',
      },
    ],
    retrieved: [productDurationOne],
  },
};

export const mockAppendOffersMonoOffer = {
  customer: false,
  offers: [mockMonoElectricityOffer],
  offerset: 'mock-offerset',
  durations: {
    present: [
      {
        duration: productDurationOne,
        durationTitle: '1 jaar',
      },
    ],
    retrieved: [productDurationOne],
  },
};

export const mockOfferDurations: GetOfferDuration[] = [
  {
    duration: productDurationOne,
    durationTitle: 'one-goat',
    offerId: 'offer-id-1',
  },
  {
    duration: productDurationFlex,
    durationTitle: 'flex-goat',
    offerId: 'offer-id-2',
  },
];
